/**
 * PerfRecorder — captures FPS / latency / jitter / first-render markers from
 * a per-frame instrument site. Designed to be zero-cost when disabled and
 * cheap (a few timestamps + ring-buffer writes) when enabled.
 *
 * Three sites must call this:
 *   1. onFrameStart(now)         — top of the rAF loop, AFTER throttle skip
 *   2. onTrackerComplete(now)    — after tracker.track() returns
 *   3. onRenderComplete(now)     — after scene.render()
 *
 * Hosts read aggregate via getSnapshot() and may subscribe to periodic
 * snapshots via onSnapshot(callback, intervalMs).
 */

export interface PerfSnapshot {
  /** Frames per second over the last rolling window (default 1s). */
  fps: number;
  /** Total frames recorded since first frame. */
  framesTotal: number;
  /** p50 of (tracker_done - frame_start) in ms. */
  trackerLatencyP50: number;
  /** p95 of tracker latency. */
  trackerLatencyP95: number;
  /** p50 of (render_done - tracker_done) in ms. */
  renderLatencyP50: number;
  /** p95 of render latency. */
  renderLatencyP95: number;
  /** End-to-end p95 (frame_start → render_done). */
  e2eLatencyP95: number;
  /** Standard deviation of inter-frame intervals in ms (jitter). */
  jitterMs: number;
  /** Wall-clock ms of the first successful render, or null if not yet rendered. */
  firstRenderMs: number | null;
}

export class PerfRecorder {
  private readonly windowSize: number;
  private frameTimes: number[] = [];
  private trackerLatencies: number[] = [];
  private renderLatencies: number[] = [];
  private e2eLatencies: number[] = [];
  private framesTotal = 0;
  private firstRenderMs: number | null = null;
  private currentFrameStart = 0;
  private currentTrackerDone = 0;
  private readonly startMs: number;

  constructor(options?: { windowSize?: number }) {
    this.windowSize = options?.windowSize ?? 120; // ~4s at 30 FPS
    this.startMs = performance.now();
  }

  onFrameStart(now: number = performance.now()): void {
    this.currentFrameStart = now;
  }

  onTrackerComplete(now: number = performance.now()): void {
    this.currentTrackerDone = now;
    const latency = now - this.currentFrameStart;
    this.pushBounded(this.trackerLatencies, latency);
  }

  onRenderComplete(now: number = performance.now()): void {
    const renderLatency = now - this.currentTrackerDone;
    const e2eLatency = now - this.currentFrameStart;
    this.pushBounded(this.renderLatencies, renderLatency);
    this.pushBounded(this.e2eLatencies, e2eLatency);
    this.pushBounded(this.frameTimes, now);
    this.framesTotal++;
    if (this.firstRenderMs === null) {
      this.firstRenderMs = now - this.startMs;
      try {
        if (typeof performance.mark === 'function') {
          performance.mark('mirrably-first-render');
        }
      } catch { /* ignore */ }
    }
  }

  getSnapshot(): PerfSnapshot {
    const fps = this.computeFps();
    const jitter = this.computeJitter();
    return {
      fps,
      framesTotal: this.framesTotal,
      trackerLatencyP50: this.percentile(this.trackerLatencies, 50),
      trackerLatencyP95: this.percentile(this.trackerLatencies, 95),
      renderLatencyP50: this.percentile(this.renderLatencies, 50),
      renderLatencyP95: this.percentile(this.renderLatencies, 95),
      e2eLatencyP95: this.percentile(this.e2eLatencies, 95),
      jitterMs: jitter,
      firstRenderMs: this.firstRenderMs,
    };
  }

  reset(): void {
    this.frameTimes = [];
    this.trackerLatencies = [];
    this.renderLatencies = [];
    this.e2eLatencies = [];
    this.framesTotal = 0;
    this.firstRenderMs = null;
  }

  // private helpers
  private pushBounded(arr: number[], value: number): void {
    arr.push(value);
    while (arr.length > this.windowSize) arr.shift();
  }

  private computeFps(): number {
    if (this.frameTimes.length < 2) return 0;
    const span = this.frameTimes[this.frameTimes.length - 1] - this.frameTimes[0];
    if (span <= 0) return 0;
    return ((this.frameTimes.length - 1) * 1000) / span;
  }

  private computeJitter(): number {
    if (this.frameTimes.length < 3) return 0;
    const deltas: number[] = [];
    for (let i = 1; i < this.frameTimes.length; i++) {
      deltas.push(this.frameTimes[i] - this.frameTimes[i - 1]);
    }
    const mean = deltas.reduce((s, x) => s + x, 0) / deltas.length;
    const variance = deltas.reduce((s, x) => s + (x - mean) ** 2, 0) / deltas.length;
    return Math.sqrt(variance);
  }

  private percentile(arr: number[], p: number): number {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
    return sorted[idx];
  }
}
